import { HighlightOff, PlaylistAddCheck } from "@mui/icons-material";
import { Badge } from "@mui/material";
import { useMotionValue } from "motion/react";
import { FC, MouseEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  FloatingMenuButton,
  FloatingMenuStyled,
  FloatingMenuTooltipWrapper,
  MuiButton,
  useFloatMenu,
} from "@ryde/controls";
import {
  useBookingsBulkActions,
  useDynamicFormsFetch,
  useModalConfirm,
  usePageContext,
  useSelectedIds,
} from "@ryde/redux";
import {
  BookingDto,
  bulkOperationsDrawerAF,
  ConfirmResult,
  getDirectionAndBookingIds,
  selectedBookingsSelector,
  selectedTableRowsIdsAtomFamily,
  useDrawers,
  useHasPermissions,
  useIsMobile,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

export const BookingsFloatingMenuGrid: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { isMobile } = useIsMobile();
  const { clearSelectedIds } = useSelectedIds(entity);
  const setBulkOperationsDrawerState = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );
  const { fetchTableRowsServerFiltered } = useDynamicFormsFetch(
    "booking",
    "bookings",
  );
  const { confirmDialogResult } = useModalConfirm();
  const { openBookingProduction } = useDrawers();
  const { hasPermissions: canProduce } = useHasPermissions({
    entity: "booking",
    action: "produce",
  });

  const { bulkArchive, bulkCancel, cleanupPreviousProductionData } =
    useBookingsBulkActions();

  const [selectedBookingIds, setSelectedBookingIds] = useRecoilState(
    selectedTableRowsIdsAtomFamily("booking"),
  );
  const bookingDtos = useRecoilValue(
    selectedBookingsSelector,
  ) as unknown as BookingDto[];
  const isOpened = Boolean(selectedBookingIds?.length);
  const {
    isInitFinished,
    leftX,
    bottomY,
    setLeftX,
    setBottomY,
    floatingMenuRef,
  } = useFloatMenu({ isOpened, page: "bookingsGrid" });

  const left = useMotionValue(leftX);
  const bottom = useMotionValue(bottomY);

  const handleArchiveClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.ARCHIVE_BOOKINGS_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const bulkArchiveResult = await bulkArchive(selectedBookingIds);

      if (bulkArchiveResult.success) {
        fetchTableRowsServerFiltered();
      }
    }
  });

  const handleProduceClick = async (
    event: MouseEvent<HTMLElement>,
  ): Promise<void> => {
    cleanupPreviousProductionData();
    const [direction] = getDirectionAndBookingIds(bookingDtos);
    const handleProduceResult = await openBookingProduction({
      bookingsIds: selectedBookingIds,
      direction,
    });

    if (handleProduceResult.success) {
      fetchTableRowsServerFiltered();
      clearSelectedIds();
    }
  };

  const handleOpenBulkOperationsDrawerClick = (): void => {
    cleanupPreviousProductionData();
    setBulkOperationsDrawerState(true);
  };

  const handleCancelClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.CANCEL_BOOKINGS_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const bulkCancelResult = await bulkCancel(selectedBookingIds);

      if (bulkCancelResult.success) {
        fetchTableRowsServerFiltered();
      }
    }
  });

  const onDragEndHandler = useEvent((): void => {
    setLeftX(left.get());
    setBottomY(bottom.get());
  });

  const onClearSelectionHandler = useEvent((): void => {
    setSelectedBookingIds([]);
  });

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    left.set(leftX);
  }, [isOpened, left, leftX]);

  return (
    isOpened && (
      <FloatingMenuStyled
        ref={floatingMenuRef}
        drag
        dragElastic={0}
        dragMomentum={false}
        onDragEnd={onDragEndHandler}
        style={{
          x: left,
          bottom,
          opacity: !isInitFinished ? 0 : "initial",
        }}
      >
        <>
          {!isMobile && <SVG_MUI.GripVertical3SVG className="pointer grip" />}

          <Badge color="secondary" badgeContent={selectedBookingIds?.length}>
            <PlaylistAddCheck style={{ color: greyMui._700 }} />
          </Badge>

          <FloatingMenuTooltipWrapper tooltip={t("booking.bulk.clear")}>
            <FloatingMenuButton onClick={onClearSelectionHandler}>
              <HighlightOff sx={{ color: greyMui._700 }} />
            </FloatingMenuButton>
          </FloatingMenuTooltipWrapper>
          {!isMobile && (
            <>
              {canProduce && (
                <FloatingMenuButton onClick={handleProduceClick}>
                  <SVG_MUI.ProductionSVG />
                  <span>{t("general.PRODUCE")}</span>
                </FloatingMenuButton>
              )}
              <MuiButton
                onClick={handleArchiveClick}
                startIcon={<SVG_MUI.ArchiveSVG />}
                muiVariant="outlined"
                muiStyle="error"
              >
                <span>{t(`general.ARCHIVE`)}</span>
              </MuiButton>

              <FloatingMenuButton onClick={handleCancelClick}>
                {t(`general.BULK_CANCEL`)}
              </FloatingMenuButton>
            </>
          )}
          <MuiButton
            muiStyle="secondary"
            className="openDrawer"
            muiSize="small"
            onClick={handleOpenBulkOperationsDrawerClick}
          >
            <SVG_MUI.EllipsisVertical3SVG color="white" />
          </MuiButton>
        </>
      </FloatingMenuStyled>
    )
  );
};
